import React, { FC, useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import styled from 'styled-components';
import { TextInputField } from '../Forms/fields';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { MfaMethodEnum } from '../../models';
import AppButton from '../AppButton';

const InfoText = styled.p`
    color: var(--text-muted);
    font-size: 0.9rem;
    text-align: center;
    margin-bottom: var(--spacer-S);
`;

const MaskedPhone = styled.span`
    font-weight: 600;
    color: var(--text-body);
`;

export interface MfaVerificationModalProps {
    isOpen: boolean;
    isLoading: boolean;
    mfaMethod: MfaMethodEnum;
    maskedPhoneNumber?: string;
    onVerify: (otp: string) => void;
    onCancel: () => void;
}

export const MfaVerificationModal: FC<MfaVerificationModalProps> = ({
    isOpen,
    isLoading,
    mfaMethod,
    maskedPhoneNumber,
    onVerify,
    onCancel,
}) => {
    const t = useTranslations();
    const [otp, setOtp] = useState('');

    const handleVerify = useCallback(() => {
        if (otp.length === 6) {
            onVerify(otp);
        }
    }, [otp, onVerify]);

    const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && otp.length === 6) {
            handleVerify();
        }
    }, [otp, handleVerify]);

    const handleClose = useCallback(() => {
        setOtp('');
        onCancel();
    }, [onCancel]);

    const canSubmit = otp.length === 6 && !isLoading;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size='md'>
            <ModalHeader toggle={handleClose}>
                {t(TK.enterVerificationCode)}
            </ModalHeader>
            <ModalBody style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                <InfoText>
                    {mfaMethod === MfaMethodEnum.Authenticator
                        ? t(TK.enterAuthenticatorCode)
                        : <>{t(TK.verificationCodeSentTo)}{' '}
                            {maskedPhoneNumber && <MaskedPhone>{maskedPhoneNumber}</MaskedPhone>}
                          </>
                    }
                </InfoText>
                <TextInputField
                    id="mfa-otp"
                    type="text"
                    placeholder="000000"
                    value={otp}
                    onChange={setOtp}
                    onKeyUp={handleKeyUp}
                    maxLength={6}
                    inputStyle={{fontSize: '2rem', textAlign: 'center', letterSpacing: '0.5rem'}}
                    style={{maxWidth: '12rem', alignSelf: 'center'}}
                    autoFocus
                />
            </ModalBody>
            <ModalFooter>
                <AppButton color="secondary" onClick={handleClose} disabled={isLoading}>
                    {t(TK.cancel)}
                </AppButton>
                <AppButton color="success" onClick={handleVerify} disabled={!canSubmit}>
                    {isLoading && <Spinner size="sm" style={{ marginRight: '0.5rem' }} />}
                    {t(TK.verify)}
                </AppButton>
            </ModalFooter>
        </Modal>
    );
};

export default MfaVerificationModal;
